import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  type Table as TanstackTable,
  useReactTable
} from "@tanstack/react-table";
import classNames from "classnames";
import find from "lodash/find";
import isFunction from "lodash/isFunction";
import map from "lodash/map";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";

import { LoadingDots } from "../loading_dots";
import { TableBody } from "./parts/table_body";
import { TableHeader } from "./parts/table_header";
import { TablePagination } from "./parts/table_pagination";
import { TableResizer } from "./parts/table_resizer";
import { generateDragHandleColumn, IsRowDragDisabledFn } from "./reorder";
import { generateSelectColumn, RowSelectionConditions } from "./selection";
import css from "./table.module.scss";
import { GenerateColumnsFn, OnRowClickFn, PaginationOptions } from "./table.types";
import { getPaginationOptions } from "./table.utils";

const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

const defaultIsRowDragDisabled = <TABLE_DATA extends TableData>(row: Row<TABLE_DATA>) =>
  !!row.original.disabled;

export type TableData = Record<string, any> & {
  disabled?: boolean;
  message?: string;
};

export type TableProps<TABLE_DATA extends TableData> = {
  className?: string;
  tableData: TABLE_DATA[];
  generateColumns: GenerateColumnsFn<TABLE_DATA>;
  footerEnabled?: boolean;
  simplePaginationEnabled?: boolean;
  simplePaginationPageSize?: number;
  onSimplePaginationChange?: (newState: PaginationState, oldState: PaginationState) => void;
  paginationOptions?: PaginationOptions;
  onRowClick?: OnRowClickFn<TABLE_DATA>;
  bottomElementsSize?: number;
  compact?: boolean;
  topAlignContent?: boolean;
  noHover?: boolean;
  displayGrid?: boolean;
  onSortingChange?: (sorting: SortingState) => void;
  controlledSorting?: SortingState;
  manualSorting?: boolean;
  initialSorting?: SortingState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
  // Required (with a stable, data-derived id) when `enableRowReordering` is true.
  getRowId?: (row: TABLE_DATA, index: number) => string;
  enableRowExpansion?: boolean;
  expandedRows?: ExpandedState;
  renderExpandedRow?: (row: TABLE_DATA) => React.ReactNode;
  enableRowSelection?: boolean | ((row: Row<TABLE_DATA>) => boolean);
  enableMultiRowSelection?: boolean;
  rowSelectionConditions?: RowSelectionConditions<TABLE_DATA>;
  enableSorting?: boolean;
  disableAutoResizing?: boolean;
  disableInternalScrolling?: boolean;
  disableHeader?: boolean;
  lastRowRef?: React.RefObject<HTMLTableRowElement | null>;
  isLoading?: boolean;
  isLoadingCompact?: boolean;
  isNestedTable?: boolean;
  selectActionsAlignment?: "start" | "center";
  scrollableContainerRef?: RefObject<HTMLElement | null>;
  hideDistantRows?: boolean;
  // Requires `getRowId` to be set with a stable, data-derived id (see below).
  enableRowReordering?: boolean;
  onRowReorder?: (newTableData: TABLE_DATA[], movedRowId: string) => void;
  isRowDragDisabled?: IsRowDragDisabledFn<TABLE_DATA>;
  // Pass below InteractiveTable props to fully support interactive table features
  tableInstanceRef?: React.RefObject<TanstackTable<any> | null>;
  isInteractive?: boolean;
  enableFixedHeader?: boolean;
  getFixedHeaderWrapperStyle?: () => Partial<CSSStyleDeclaration> | undefined;
};

const Table = <TABLE_DATA extends TableData>({
  className,
  tableData,
  generateColumns,
  simplePaginationEnabled,
  simplePaginationPageSize,
  onSimplePaginationChange,
  paginationOptions,
  onRowClick,
  bottomElementsSize,
  compact,
  topAlignContent,
  noHover,
  displayGrid,
  onSortingChange,
  manualSorting = !!onSortingChange,
  controlledSorting,
  initialSorting,
  onRowSelectionChange,
  rowSelection,
  getRowId,
  enableRowSelection,
  enableRowExpansion,
  expandedRows,
  renderExpandedRow,
  enableMultiRowSelection,
  rowSelectionConditions,
  enableSorting = true,
  disableAutoResizing,
  disableInternalScrolling,
  disableHeader,
  lastRowRef,
  isLoading,
  isLoadingCompact,
  isNestedTable,
  selectActionsAlignment = "center",
  scrollableContainerRef,
  hideDistantRows,
  enableRowReordering,
  onRowReorder,
  isRowDragDisabled = defaultIsRowDragDisabled,
  tableInstanceRef,
  isInteractive,
  enableFixedHeader,
  getFixedHeaderWrapperStyle
}: TableProps<TABLE_DATA>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [expanded, setExpanded] = useState<ExpandedState>(expandedRows ?? {});
  const [simplePagination, setSimplePagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: simplePaginationPageSize ?? 50
  });
  const tableRef = useRef<HTMLTableElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  const { current: columnHelper } = useRef(createColumnHelper<TABLE_DATA>());
  const columns = useMemo(() => generateColumns(columnHelper), [columnHelper, generateColumns]);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS));

  useEffect(() => {
    if (controlledSorting) {
      setSorting(controlledSorting);
    }
  }, [controlledSorting]);

  useEffect(() => {
    if (expandedRows) {
      setExpanded(expandedRows);
    }
  }, [expandedRows]);

  const changeSorting: OnChangeFn<SortingState> = (sort) => {
    const unwrappedSort = typeof sort === "function" ? sort(sorting) : sort;
    onSortingChange?.(unwrappedSort);

    if (!controlledSorting) {
      setSorting(unwrappedSort);
    }
  };

  const columnsWithEnabledFeatures = useMemo(
    () =>
      [
        generateDragHandleColumn<TABLE_DATA>(columnHelper, !!enableRowReordering, isRowDragDisabled),
        generateSelectColumn<TABLE_DATA>(
          columnHelper,
          !!enableRowSelection,
          !!enableMultiRowSelection,
          !!isNestedTable || enableRowExpansion,
          selectActionsAlignment,
          rowSelectionConditions
        ),
        ...columns
      ]
        .filter((column) => column !== null)
        // setting sortDescFirst is an official workaround
        // for properly toggling sort state on columns with nullable values
        // as explained here: https://github.com/TanStack/table/issues/4289 and advised here: https://tanstack.com/table/v8/docs/guide/sorting
        .map((column) => ({ enableMultiSort: false, sortDescFirst: true, ...column })),
    [
      columnHelper,
      columns,
      enableRowReordering,
      isRowDragDisabled,
      enableRowSelection,
      enableMultiRowSelection,
      isNestedTable,
      enableRowExpansion,
      selectActionsAlignment,
      rowSelectionConditions
    ]
  );

  const table = useReactTable({
    data: tableData,
    columns: columnsWithEnabledFeatures,
    defaultColumn: {
      minSize: 0,
      size: 0
    },
    state: {
      sorting,
      rowSelection,
      pagination: simplePagination,
      expanded
    },
    onSortingChange: changeSorting,
    onPaginationChange: (valueOrUpdater) => {
      setSimplePagination((oldState) => {
        const newState = isFunction(valueOrUpdater) ? valueOrUpdater(oldState) : valueOrUpdater;
        onSimplePaginationChange?.(newState, oldState);
        return newState;
      });
    },
    enableRowSelection: enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: simplePaginationEnabled ? getPaginationRowModel() : undefined,
    manualSorting,
    onRowSelectionChange,
    getRowId,
    enableMultiRowSelection: !!enableMultiRowSelection,
    enableSorting,
    getRowCanExpand: () => enableRowExpansion || false,
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded
  });

  useEffect(() => {
    if (tableInstanceRef) {
      tableInstanceRef.current = table;

      return () => {
        tableInstanceRef.current = null;
      };
    }
  }, [table, tableInstanceRef]);

  const isPaginationEnabled = simplePaginationEnabled || paginationOptions;
  const { rows } = table.getRowModel();
  const rowIds = map(rows, "id");

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeRow = find(rows, ["id", String(active.id)]);
    const overRow = find(rows, ["id", String(over.id)]);

    if (!activeRow || !overRow) {
      return;
    }

    const activeIndex = tableData.indexOf(activeRow.original);
    const overIndex = tableData.indexOf(overRow.original);

    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    const newTableData = arrayMove(tableData, activeIndex, overIndex);

    onRowReorder?.(newTableData, String(active.id));
  };

  const tableElement = (
    <table ref={tableRef} className={classNames(className, css.root)}>
      {!disableHeader && (
        <TableHeader<TABLE_DATA>
          headerGroups={table.getHeaderGroups()}
          compact={compact}
          displayGrid={displayGrid}
          enableMultiRowSelection={enableMultiRowSelection}
          enableFixedHeader={enableFixedHeader}
          tableClassName={classNames(className, css.root)}
          tableRef={tableRef}
          scrollContainerRef={scrollableContainerRef}
          getFixedHeaderWrapperStyle={getFixedHeaderWrapperStyle}
        />
      )}
      <TableBody<TABLE_DATA>
        rows={rows}
        compact={compact}
        topAlignContent={topAlignContent}
        noHover={noHover}
        displayGrid={displayGrid}
        onRowClick={onRowClick}
        lastRowRef={lastRowRef}
        renderExpandedRow={renderExpandedRow}
        rowSelectionConditions={rowSelectionConditions}
        scrollableContainerRef={scrollableContainerRef}
        hideDistantRows={hideDistantRows}
        isInteractive={isInteractive}
        enableRowReordering={enableRowReordering}
        isRowDragDisabled={isRowDragDisabled}
      />
    </table>
  );

  return (
    <>
      <TableResizer
        tableRef={tableRef}
        paginationRef={paginationRef}
        bottomElementsSize={bottomElementsSize}
        disableAutoResizing={disableAutoResizing}
        disableInternalScrolling={disableInternalScrolling}
      >
        {isLoading && (
          <div className={classNames(css.loadingOverlay, { [css.loadingOverlayCompact]: isLoadingCompact })}>
            <LoadingDots circlesEnabled circleSize="md" />
          </div>
        )}
        {enableRowReordering ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
              {tableElement}
            </SortableContext>
          </DndContext>
        ) : (
          tableElement
        )}
      </TableResizer>

      {isPaginationEnabled && (
        <TablePagination
          paginationRef={paginationRef}
          paginationOptions={getPaginationOptions<TABLE_DATA>(table, paginationOptions)}
        />
      )}
    </>
  );
};

export default Table;
